import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";
import { useCallback, useEffect, useRef } from "react";
// @mui
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Drawer from "@mui/material/Drawer";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { TreeView } from "devextreme-react";
import Submit from "src/consuman-components/generic/submit";
import CustomBreadcrumbs from "src/components/custom-breadcrumbs";
// hooks
import { useBoolean } from "src/hooks/use-boolean";
// locales
import { useLocales } from "src/locales";
// api
import { useGetPlainTree as useGetPlainTreeCategory } from "src/api/category";

import { useFieldConfiguration } from "src/consuman-components/generic/context/fieldConfigurationContext";

export const ROOT_CATEGORY_ID = "00000000-0000-0000-0000-000000000000";

// Ítem del árbol de categorías. El nombre se recorta con puntos suspensivos y el nombre completo
// queda en el tooltip: el árbol se muestra en un drawer angosto y, sin recorte, los nombres largos
// se desbordaban del chip y los tapaba el overflow del contenedor del árbol, sin ningún indicio de
// que faltaba texto.
export function CategoryTreeItem({ item }) {
  const { t } = useLocales();

  const name = item.id === ROOT_CATEGORY_ID ? t(item.name) : item.name;

  return (
    <Tooltip
      title={name}
      placement="top"
    >
      <Chip
        icon={
          // El nombre viaja en el slot del icono, que no recorta por sí solo: sin permitirle achicarse
          // el chip crece con el texto en lugar de ceder el ancho sobrante al recorte.
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ minWidth: 0, overflow: "hidden" }}
          >
            {item.avatarUri ? (
              <Avatar
                alt="avatar"
                src={item.avatarUri}
              />
            ) : (
              <Avatar>
                {item.name
                  .split(" ")
                  .map(word => word[0])
                  .slice(0, 2)
                  .join("")}
              </Avatar>
            )}
            <Box
              sx={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Box>
          </Stack>
        }
        color="primary"
        variant="soft"
        size="medium"
      />
    </Tooltip>
  );
}

CategoryTreeItem.propTypes = {
  item: PropTypes.object,
};

export default function CategoryTreeView({
  disabled,
  onChange,
  name,
  label,
  type,
  disableClearable = true,
  disableContainer = false,
  disabledId = null,
  multiple = false,
  onlyContainer = false,
}) {
  const treeViewRef = useRef(null);

  const fieldConfiguration = useFieldConfiguration()?.getFieldByName(name);
  const canView = fieldConfiguration == null || fieldConfiguration?.canView;
  const { control, setValue, getValues } = useFormContext();

  const { t } = useLocales();

  const drawer = useBoolean(false);
  const {
    tree: dataSource,
    isLoading,
    isValidating,
  } = useGetPlainTreeCategory(drawer.value, onlyContainer, disableContainer);

  const disableChildrenRecursive = useCallback(
    (node, disable) => {
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          const childNode = treeViewRef.current.instance._userOptions.dataSource.find(
            x => x.id === child.key
          );
          childNode.disabledItem = true;
          childNode.selected = false;
          disableChildrenRecursive(child, disable);
        });
      }
    },
    [treeViewRef]
  ); // Depende de treeViewRef.current

  const handleSelectionChanged = useCallback(
    e => {
      const selectedNodes = treeViewRef.current.instance.getSelectedNodes();
      if (!disableContainer || selectedNodes.find(x => x.itemData.isContainer === false)) {
        if (multiple) {
          treeViewRef.current.instance._userOptions.dataSource.forEach(x => {
            x.disabledItem = false;
          });

          selectedNodes.forEach(selectedNode => {
            disableChildrenRecursive(selectedNode, selectedNode.selected);
          });
        }

        treeViewRef.current.instance.option(
          "dataSource",
          treeViewRef.current.instance._userOptions.dataSource
        );

        if (drawer.value && !multiple) {
          const nodes = treeViewRef.current.instance.getSelectedNodes().map(n => n.itemData);

          const selected = nodes[0];

          if (nodes.length > 0) setValue(name, selected, { shouldValidate: true });

          if (nodes.length > 0) drawer.onFalse();

          if (onChange) onChange(selected);
        }
      }
    },
    [name, multiple, setValue, drawer, disableContainer, onChange, disableChildrenRecursive]
  );

  const handleSelectionsChanged = useCallback(
    e => {
      if (drawer.value) {
        const nodes = treeViewRef.current.instance.getSelectedNodes().map(n => n.itemData);

        const selected = multiple ? nodes : nodes[0];

        if (nodes.length > 0) setValue(name, selected, { shouldValidate: true });

        if (nodes.length > 0) drawer.onFalse();
      }
    },
    [name, multiple, setValue, drawer]
  );

  useEffect(() => {
    if (treeViewRef.current?.instance && drawer.value && dataSource.length > 0) {
      const values = getValues(name);

      treeViewRef.current.instance.beginUpdate();
      treeViewRef.current.instance.unselectAll();

      if (multiple) {
        // const values = getValues(name);

        values?.forEach(n => treeViewRef.current.instance.selectItem(n.id));
      }

      treeViewRef.current.instance.endUpdate();
    }
  }, [drawer.value, treeViewRef.current, dataSource, multiple, name, getValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemRender = useCallback(item => <CategoryTreeItem item={item} />, []);

  return (
    <>
      {canView && (
        <>
          <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                fullWidth
                open={false}
                disabled={disabled}
                forcePopupIcon={!disabled}
                onOpen={drawer.onTrue}
                options={[]}
                disableClearable={disableClearable}
                multiple={multiple}
                onChange={(event, newValue) => setValue(name, newValue, { shouldValidate: true })}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={option =>
                  option?.id === ROOT_CATEGORY_ID ? t(option.name) : option?.name
                }
                renderInput={params => (
                  <TextField
                    label={label || t("category")}
                    error={!!error}
                    helperText={error?.message}
                    {...params}
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.id === ROOT_CATEGORY_ID ? t(option.name) : option.name}
                      size="small"
                    />
                  ))
                }
              />
            )}
          />

          <Drawer
            key={name}
            anchor="right"
            open={drawer.value}
            onClose={drawer.onFalse}
            PaperProps={{
              sx: { width: "35%" },
            }}
          >
            <Container sx={{ marginTop: "3%" }}>
              <CustomBreadcrumbs
                heading={label || t("category")}
                action={
                  <Submit
                    onClick={drawer.onFalse}
                    onClickSave={handleSelectionsChanged}
                    showSave={multiple}
                    margin="0"
                    back
                  />
                }
                sx={{ mb: { xs: 3, md: 2 } }}
              />
              {isLoading || isValidating ? (
                <Stack
                  sx={{ position: "relative" }}
                  height="100%"
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircularProgress variant="indeterminate" />
                </Stack>
              ) : (
                <TreeView
                  ref={treeViewRef}
                  id={name}
                  key={name}
                  dataStructure="plain"
                  dataSource={dataSource.filter(x => x.id !== disabledId)}
                  searchEnabled
                  selectNodesRecursive={false}
                  selectionMode={multiple ? "multiple" : "single"}
                  showCheckBoxesMode={multiple ? "normal" : "none"}
                  selectByClick
                  rootValue={null}
                  itemRender={itemRender}
                  keyExpr="id"
                  parentIdExpr="parentId"
                  displayExpr="name"
                  disabledExpr="disabledItem"
                  expandedExpr="expanded"
                  onSelectionChanged={handleSelectionChanged}
                />
              )}
            </Container>
          </Drawer>
        </>
      )}
    </>
  );
}

CategoryTreeView.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.string,
  disabledId: PropTypes.any,
  disableContainer: PropTypes.any,
  onChange: PropTypes.any,
  onlyContainer: PropTypes.any,
  type: PropTypes.any,
  disableClearable: PropTypes.bool,
  multiple: PropTypes.bool,
};
